import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import Sidebar from "@/components/feature/Sidebar";
import { titleForPath } from "@/components/feature/navigation";
import ToastContainer from "@/components/feature/ToastContainer";
import { useToast } from "@/store/ToastContext";
import { settingsApi } from "@/services/api";
import type { AppSettings } from "@/models/types";

const BOTTOM_NAV = [
  { path: "/", label: "Inicio", icon: "ri-dashboard-3-line" },
  { path: "/gastos", label: "Gastos", icon: "ri-wallet-3-line" },
  { path: "/recibos", label: "Recibos", icon: "ri-receipt-line" },
  { path: "/estadisticas", label: "Stats", icon: "ri-bar-chart-2-line" },
];

const DEFAULT_SETTINGS: AppSettings = {
  id: "settings",
  currency: "USD",
  language: "es",
  theme: "light",
  userName: "Usuario",
  monthlyBudget: 0,
  notifications: { weeklySummary: true, budgetAlert: true },
};

function ThemeToggle({
  isDark,
  onToggle,
}: {
  isDark: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-secondary-100 text-foreground-700 transition-colors cursor-pointer"
      aria-label="Cambiar tema"
    >
      <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-lg`} />
    </button>
  );
}

export default function AppLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { showToast } = useToast();

  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
  if (settings.theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}, [settings.theme]);
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await settingsApi.get();
        setSettings({ ...DEFAULT_SETTINGS, ...data });
      } catch (error) {
        console.error("Error cargando configuración:", error);
        showToast("Usando configuración por defecto.", "info");
      } finally {
        setLoadingSettings(false);
      }
    };
    loadSettings();
  }, [showToast]);

const toggleTheme = async () => {
  const nextTheme = settings.theme === 'dark' ? 'light' : 'dark';
  // Aplicar clase al HTML de forma optimista
  document.documentElement.classList.toggle('dark', nextTheme === 'dark');
  setSettings(prev => ({ ...prev, theme: nextTheme }));
  try {
    const updated = await settingsApi.update({ theme: nextTheme });
    setSettings(prev => ({ ...prev, ...updated }));
  } catch (error) {
    // Revertir si falla
    document.documentElement.classList.toggle('dark', settings.theme === 'dark');
    setSettings(prev => ({ ...prev, theme: settings.theme }));
    showToast("No se pudo guardar el tema.", "error");
  }
};

  return (
    <div className="min-h-screen bg-background-50">
      <Sidebar />

      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-background-50/90 backdrop-blur border-b border-background-200">
          <div className="flex items-center justify-between h-14 px-4 md:px-6">
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="lg:hidden w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-wallet-3-fill text-white text-lg" />
              </Link>
              <h1 className="text-base md:text-lg font-heading font-bold text-foreground-950">
                {titleForPath(location.pathname)}
              </h1>
            </div>

            <div className="flex items-center gap-1">
              <ThemeToggle
                isDark={settings.theme === "dark"}
                onToggle={toggleTheme}
              />
              {/* Menú móvil para Categorías y Configuración */}
              <div className="relative lg:hidden">
                <button
                  onClick={() => setMoreOpen((v) => !v)}
                  className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-secondary-100 text-foreground-700 cursor-pointer"
                >
                  <i className="ri-more-2-fill text-lg" />
                </button>
                {moreOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setMoreOpen(false)}
                    />
                    <div className="absolute right-0 mt-1 w-48 bg-background-50 border border-background-200 rounded-lg shadow-sm z-50 py-1 animate-fade-in-up">
                      <Link
                        to="/categorias"
                        onClick={() => setMoreOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-price-tag-3-line" />
                        Categorías
                      </Link>
                      <Link
                        to="/configuracion"
                        onClick={() => setMoreOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-settings-3-line" />
                        Configuración
                      </Link>
                    </div>
                  </>
                )}
              </div>
              <div className="hidden lg:flex w-9 h-9 rounded-full bg-primary-100 text-primary-700 items-center justify-center font-semibold">
                {settings.userName.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Contenido */}
        <main className="flex-1 w-full px-4 md:px-6 py-5 md:py-7 pb-28 lg:pb-12">
          <div className="max-w-6xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Bottom nav + FAB (móvil) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50 border-t border-background-200">
        <div className="grid grid-cols-5 items-center h-16 px-1">
          {BOTTOM_NAV.slice(0, 2).map((item) => (
            <BottomNavItem key={item.path} {...item} />
          ))}
          <div className="flex justify-center relative">
            <Link
              to="/gastos/nuevo"
              className="w-14 h-14 -mt-8 rounded-full bg-primary-500 text-white flex items-center justify-center shadow-lg hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-3xl" />
            </Link>
          </div>
          {BOTTOM_NAV.slice(2).map((item) => (
            <BottomNavItem key={item.path} {...item} />
          ))}
        </div>
      </nav>

      <ToastContainer />
    </div>
  );
}

function BottomNavItem({
  path,
  label,
  icon,
}: {
  path: string;
  label: string;
  icon: string;
}) {
  return (
    <NavLink
      to={path}
      end={path === "/"}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 py-1 text-[10px] font-medium transition-colors cursor-pointer ${
          isActive ? "text-primary-600" : "text-foreground-500"
        }`
      }
    >
      <span className="w-6 h-6 flex items-center justify-center">
        <i className={`${icon} text-xl`} />
      </span>
      <span className="whitespace-nowrap">{label}</span>
    </NavLink>
  );
}